import React, { useState } from 'react';
import { X, Check, ArrowRight, Kanban, FileText, Package, DollarSign, CheckCircle, Eye, Upload, Move, Target, Zap } from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';

interface ApplicationsOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const ApplicationsOnboardingModal: React.FC<ApplicationsOnboardingModalProps> = ({ isOpen, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const { updateOnboardingStatus } = useTenant();

  const steps = [
    {
      title: "¡Bienvenido a Postulaciones!",
      subtitle: "Tu centro de control para gestionar oportunidades de licitación",
      content: (
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full mb-6">
            <Kanban className="w-10 h-10 text-white" />
          </div>
          <p className="text-gray-600 text-lg leading-relaxed mb-6">
            Aquí encontrarás las licitaciones en las cuales <strong>decidiste participar</strong>. 
            Es un sistema <strong>Kanban</strong> que te permite gestionar cada oportunidad 
            desde la decisión inicial hasta el resultado final.
          </p>
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <Zap className="h-5 w-5 text-purple-600 mt-0.5" />
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-purple-900 mb-1">
                  Gestión Centralizada
                </h4>
                <p className="text-sm text-purple-700">
                  Todas tus oportunidades de licitación en un solo lugar, 
                  con seguimiento completo del proceso y documentos centralizados.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "¿Qué es un Kanban?",
      subtitle: "Entiende cómo funciona este sistema de gestión visual",
      content: (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-full mb-4">
              <Kanban className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Sistema Kanban</h3>
            <p className="text-gray-600">
              Un método visual para gestionar el flujo de trabajo, donde cada oportunidad 
              se mueve a través de diferentes columnas según su estado.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Columna 1 */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full mr-3">
                  <Eye className="w-4 h-4 text-blue-600" />
                </div>
                <h3 className="font-semibold text-blue-900">En Evaluación</h3>
              </div>
              <p className="text-sm text-blue-700 mb-3">
                Oportunidades que estás analizando para decidir si participar.
              </p>
              <div className="bg-blue-100 rounded-lg p-2">
                <p className="text-xs text-blue-800 font-medium">
                  📋 Revisar requisitos<br/>
                  💰 Evaluar viabilidad<br/>
                  📅 Verificar fechas
                </p>
              </div>
            </div>

            {/* Columna 2 */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <div className="flex items-center justify-center w-8 h-8 bg-green-100 rounded-full mr-3">
                  <FileText className="w-4 h-4 text-green-600" />
                </div>
                <h3 className="font-semibold text-green-900">En Preparación</h3>
              </div>
              <p className="text-sm text-green-700 mb-3">
                Oportunidades que decidiste participar y estás preparando la oferta.
              </p>
              <div className="bg-green-100 rounded-lg p-2">
                <p className="text-xs text-green-800 font-medium">
                  📄 Cargar documentos<br/>
                  💼 Agregar productos<br/>
                  💵 Calcular costos
                </p>
              </div>
            </div>

            {/* Columna 3 */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <div className="flex items-center justify-center w-8 h-8 bg-purple-100 rounded-full mr-3">
                  <Move className="w-4 h-4 text-purple-600" />
                </div>
                <h3 className="font-semibold text-purple-900">Ofertada/Descartada</h3>
              </div>
              <p className="text-sm text-purple-700 mb-3">
                Oportunidades ya enviadas o descartadas, esperando resultado.
              </p>
              <div className="bg-purple-100 rounded-lg p-2">
                <p className="text-xs text-purple-800 font-medium">
                  ✅ Oferta enviada<br/>
                  ❌ Descartada<br/>
                  ⏳ Esperando resultado
                </p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-semibold text-yellow-900 mb-2">💡 Cómo mover oportunidades:</h4>
            <p className="text-sm text-yellow-700">
              Simplemente <strong>arrastra y suelta</strong> cada tarjeta entre las columnas 
              para cambiar su estado, o usa los botones de acción disponibles.
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Gestión de Documentos",
      subtitle: "Centraliza todos los documentos que requieren firma",
      content: (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-red-600 rounded-full mb-4">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Documentos Centralizados</h3>
            <p className="text-gray-600">
              Sube y organiza todos los documentos necesarios para cada licitación 
              en un solo lugar, facilitando la gestión y firma.
            </p>
          </div>

          <div className="space-y-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <Upload className="w-5 h-5 text-orange-600 mt-0.5" />
                </div>
                <div className="ml-3">
                  <h4 className="font-semibold text-orange-900 mb-2">Subir Documentos</h4>
                  <p className="text-sm text-orange-700 mb-3">
                    Carga todos los archivos requeridos por la licitación: 
                    certificados, declaraciones, propuestas técnicas, etc.
                  </p>
                  <div className="bg-orange-100 rounded-lg p-3">
                    <p className="text-xs text-orange-800">
                      <strong>📋 Tipos de documentos:</strong><br/>
                      • Certificados de la empresa<br/>
                      • Declaraciones juradas<br/>
                      • Propuestas técnicas<br/>
                      • Documentos financieros<br/>
                      • Otros requisitos específicos
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                </div>
                <div className="ml-3">
                  <h4 className="font-semibold text-blue-900 mb-2">Control de Firmas</h4>
                  <p className="text-sm text-blue-700 mb-3">
                    Identifica qué documentos requieren firma y mantén un control 
                    de cuáles ya están firmados y cuáles faltan.
                  </p>
                  <div className="bg-blue-100 rounded-lg p-3">
                    <p className="text-xs text-blue-800">
                      <strong>✅ Beneficios:</strong><br/>
                      • No olvides documentos pendientes<br/>
                      • Control visual del estado<br/>
                      • Preparación más eficiente<br/>
                      • Menos errores en la presentación
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <Target className="w-5 h-5 text-green-600 mt-0.5" />
                </div>
                <div className="ml-3">
                  <h4 className="font-semibold text-green-900 mb-2">Organización Inteligente</h4>
                  <p className="text-sm text-green-700">
                    Los documentos se organizan automáticamente por tipo y estado, 
                    facilitando la revisión y preparación final de la oferta.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Productos y Costos",
      subtitle: "Agrega productos para determinar el costo de tu propuesta",
      content: (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full mb-4">
              <Package className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Cálculo de Costos</h3>
            <p className="text-gray-600">
              Agrega los productos y servicios necesarios para calcular 
              el costo total de tu propuesta de manera precisa.
            </p>
          </div>

          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <Package className="w-5 h-5 text-green-600 mt-0.5" />
                </div>
                <div className="ml-3">
                  <h4 className="font-semibold text-green-900 mb-2">Agregar Productos</h4>
                  <p className="text-sm text-green-700 mb-3">
                    Selecciona los productos y servicios de tu catálogo que 
                    necesitas para cumplir con los requisitos de la licitación.
                  </p>
                  <div className="bg-green-100 rounded-lg p-3">
                    <p className="text-xs text-green-800">
                      <strong>📦 Productos disponibles:</strong><br/>
                      • Los que tienes registrados en tu catálogo<br/>
                      • Con precios y especificaciones actualizadas<br/>
                      • Fácil selección desde una lista filtrada
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <DollarSign className="w-5 h-5 text-blue-600 mt-0.5" />
                </div>
                <div className="ml-3">
                  <h4 className="font-semibold text-blue-900 mb-2">Cálculo Automático</h4>
                  <p className="text-sm text-blue-700 mb-3">
                    El sistema calcula automáticamente el costo total considerando 
                    cantidades, precios unitarios, impuestos y márgenes.
                  </p>
                  <div className="bg-blue-100 rounded-lg p-3">
                    <p className="text-xs text-blue-800">
                      <strong>💰 Incluye:</strong><br/>
                      • Precio unitario de cada producto<br/>
                      • Cantidades requeridas<br/>
                      • Impuestos aplicables<br/>
                      • Márgenes de ganancia<br/>
                      • Costos adicionales (transporte, etc.)
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5" />
                </div>
                <div className="ml-3">
                  <h4 className="font-semibold text-purple-900 mb-2">Propuesta Competitiva</h4>
                  <p className="text-sm text-purple-700">
                    Con el costo calculado, puedes ajustar márgenes y crear 
                    una propuesta competitiva que sea rentable para tu empresa.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Flujo Completo",
      subtitle: "Desde la decisión hasta el resultado final",
      content: (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full mb-4">
              <Move className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Proceso Completo</h3>
            <p className="text-gray-600">
              Sigue el flujo completo desde que decides participar 
              hasta que recibes el resultado de la adjudicación.
            </p>
          </div>

          <div className="space-y-4">
            {/* Paso 1 */}
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
                  <span className="text-lg font-bold text-blue-600">1</span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Decisión de Participar</h3>
                <p className="text-gray-600 mb-3">
                  Desde "Licitaciones", generas una oportunidad y la mueves a "En Evaluación" 
                  para analizar si vale la pena participar.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>📋 Evaluar:</strong> Requisitos técnicos, viabilidad económica, fechas, competencia
                  </p>
                </div>
              </div>
            </div>

            {/* Paso 2 */}
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-full">
                  <span className="text-lg font-bold text-green-600">2</span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Preparación de Oferta</h3>
                <p className="text-gray-600 mb-3">
                  Si decides participar, mueves la oportunidad a "En Preparación" 
                  y cargas documentos, agregas productos y calculas costos.
                </p>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800">
                    <strong>📄 Preparar:</strong> Documentos requeridos, productos necesarios, cálculo de costos
                  </p>
                </div>
              </div>
            </div>

            {/* Paso 3 */}
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-full">
                  <span className="text-lg font-bold text-purple-600">3</span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Oferta Enviada o Descartada</h3>
                <p className="text-gray-600 mb-3">
                  Una vez preparada, mueves la oportunidad a "Ofertada" si enviaste la propuesta, 
                  o a "Descartada" si decidiste no participar.
                </p>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-sm text-purple-800">
                    <strong>📤 Resultado:</strong> Oferta enviada exitosamente o decisión de no participar
                  </p>
                </div>
              </div>
            </div>

            {/* Paso 4 */}
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center w-10 h-10 bg-orange-100 rounded-full">
                  <span className="text-lg font-bold text-orange-600">4</span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Esperar Resultado</h3>
                <p className="text-gray-600 mb-3">
                  El sistema te notificará automáticamente cuando se publique el resultado 
                  de la adjudicación, indicando si ganaste o perdiste.
                </p>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <p className="text-sm text-orange-800">
                    <strong>⏳ Esperar:</strong> Notificación automática del resultado de adjudicación
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4">
            <h4 className="font-semibold text-indigo-900 mb-2">🎯 Resumen del Flujo:</h4>
            <p className="text-sm text-indigo-700">
              <strong>En Evaluación</strong> → <strong>En Preparación</strong> → <strong>Ofertada/Descartada</strong> → <strong>Resultado Final</strong>
            </p>
            <p className="text-xs text-indigo-600 mt-2">
              Cada paso te permite gestionar eficientemente tus oportunidades y mantener 
              un control completo del proceso de licitación.
            </p>
          </div>
        </div>
      )
    },
    {
      title: "¡Listo para gestionar!",
      subtitle: "Ya conoces todo el sistema Kanban de postulaciones",
      content: (
        <div className="text-center space-y-6">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full mb-4">
            <Check className="w-10 h-10 text-white" />
          </div>
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900">¡Gestiona tus oportunidades!</h3>
            <p className="text-gray-600 text-lg">
              Ahora puedes usar el sistema Kanban para gestionar todas tus postulaciones, 
              desde la evaluación inicial hasta el resultado final.
            </p>
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
              <p className="text-sm text-purple-700">
                <strong>🎯 Recuerda:</strong> Arrastra y suelta las tarjetas entre columnas para cambiar 
                el estado de tus oportunidades, y mantén todos los documentos organizados 
                para una gestión eficiente.
              </p>
            </div>
          </div>
        </div>
      )
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    console.log('🎯 Applications Onboarding - handleComplete called, dontShowAgain:', dontShowAgain);
    if (dontShowAgain) {
      try {
        console.log('🔄 Marking Onboarding3 as completed');
        await updateOnboardingStatus('Onboarding3', true);
      } catch (error) {
        console.error('Error updating onboarding status:', error);
      }
    }
    onComplete();
  };

  const handleSkip = async () => {
    console.log('⏭️ Applications Onboarding - handleSkip called, dontShowAgain:', dontShowAgain);
    if (dontShowAgain) {
      try {
        console.log('🔄 Marking Onboarding3 as completed');
        await updateOnboardingStatus('Onboarding3', true);
      } catch (error) {
        console.error('Error updating onboarding status:', error);
      }
    }
    onComplete();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-700 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">{steps[currentStep].title}</h2>
              <p className="text-purple-100 text-sm">{steps[currentStep].subtitle}</p>
            </div>
            <button
              onClick={handleSkip}
              className="text-purple-200 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Progress bar */}
          <div className="mt-4">
            <div className="flex space-x-2">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 flex-1 rounded-full transition-colors ${
                    index <= currentStep ? 'bg-white' : 'bg-purple-300'
                  }`}
                />
              ))}
            </div>
            <p className="text-purple-100 text-xs mt-2">
              Paso {currentStep + 1} de {steps.length}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-96 overflow-y-auto">
          {steps[currentStep].content}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="dontShowAgainApplications"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <label htmlFor="dontShowAgainApplications" className="ml-2 text-sm text-gray-600">
                No mostrar más este tutorial
              </label>
            </div>
            
            <div className="flex space-x-3">
              {currentStep > 0 && (
                <button
                  onClick={handlePrevious}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Anterior
                </button>
              )}
              
              <button
                onClick={handleNext}
                className="bg-purple-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center"
              >
                {currentStep === steps.length - 1 ? 'Comenzar' : 'Siguiente'}
                {currentStep < steps.length - 1 && <ArrowRight className="w-4 h-4 ml-2" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationsOnboardingModal;
